import React, { useRef, useEffect } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ArrowRight, Sparkles, Zap, Palette, Box } from 'lucide-react';
import { Link } from 'react-router-dom';
import { studioInfo, services, testimonials } from '../data/mock';
import useSEO from '../hooks/useSEO';
import { ROUTE_DEFAULTS, SITE_URL } from '../config/seo';
import SchemaOrg, {
  buildOrganizationSchema,
  buildProfessionalServiceSchema,
  buildReviewSchema,
} from '../components/SchemaOrg';
import FAQ from '../components/FAQ';
import LeadCaptureForm from '../components/LeadCaptureForm';
import { homepageFaqs } from '../data/faqs';

const CULTIV_POSITIONING =
  'Cultiv AI Studio is an AI-first creative agency that produces viral video, image, and brand campaigns for B2B SaaS, DTC, and early-stage teams — combining generative AI pipelines with senior creative direction to deliver ads, UGC, brand systems, and product visuals in days instead of months.';

gsap.registerPlugin(ScrollTrigger);

const Home = () => {
  const featuredServices = services.slice(0, 3);

  useSEO({
    ...ROUTE_DEFAULTS['/'],
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'ItemList',
      '@id': `${SITE_URL}/#featured-services`,
      name: 'Featured Cultiv AI Studio services',
      itemListOrder: 'https://schema.org/ItemListOrderAscending',
      numberOfItems: featuredServices.length,
      itemListElement: featuredServices.map((service, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@type': 'Service',
          name: service.title,
          description: service.description,
          provider: { '@id': `${SITE_URL}/#organization` },
          areaServed: 'Worldwide',
        },
      })),
    },
  });

  const reviewTestimonials = testimonials
    .filter((t) => t && t.name && t.quote)
    .slice(0, 3);

  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start']
  });

  const opacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.5], [1, 0.95]);
  const y = useTransform(scrollYProgress, [0, 0.5], [0, 50]);

  useEffect(() => {
    // GSAP smooth scroll animations
    gsap.fromTo(
      '.service-card',
      { opacity: 0, y: 60, scale: 0.95 },
      {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 1.5,
        stagger: 0.15,
        ease: 'power2.inOut',
        scrollTrigger: {
          trigger: '.services-section',
          start: 'top 85%',
          end: 'top 30%',
          scrub: 0.5,
          toggleActions: 'play none none reverse'
        }
      }
    );

    gsap.fromTo(
      '.testimonial-card',
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 1.2,
        stagger: 0.12,
        ease: 'power2.inOut',
        scrollTrigger: {
          trigger: '.testimonials-section',
          start: 'top 85%',
          end: 'top 30%',
          scrub: 0.4,
          toggleActions: 'play none none reverse'
        }
      }
    );

    return () => {
      ScrollTrigger.getAll().forEach(trigger => trigger.kill());
    };
  }, []);

  return (
    <>
      <SchemaOrg
        id="home-organization"
        data={buildOrganizationSchema({
          description: CULTIV_POSITIONING,
          sameAs: [],
        })}
      />
      <SchemaOrg
        id="home-professional-service"
        data={buildProfessionalServiceSchema({
          description: CULTIV_POSITIONING,
          serviceType: 'AI Creative Production',
          areaServed: 'Worldwide',
        })}
      />
      {reviewTestimonials.map((testimonial, index) => {
        const reviewSchema = buildReviewSchema({
          author: testimonial.name,
          reviewBody: testimonial.quote,
          reviewRating: { ratingValue: 5, bestRating: 5 },
          itemReviewed: {
            '@type': 'ProfessionalService',
            '@id': `${SITE_URL}/#service`,
            name: 'Cultiv AI Studio',
          },
        });
        if (!reviewSchema) return null;
        return (
          <SchemaOrg
            key={`home-review-${testimonial.id ?? index + 1}`}
            id={`home-review-${index + 1}`}
            data={reviewSchema}
          />
        );
      })}
    <div className="bg-black text-white">
      {/* Hero Section */}
      <section ref={containerRef} className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated neon geometric shapes */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <motion.div
            animate={{
              rotate: [0, 360],
              scale: [1, 1.2, 1],
            }}
            transition={{
              duration: 25,
              repeat: Infinity,
              ease: "linear"
            }}
            className="absolute top-1/4 left-1/4 w-32 h-32 sm:w-48 sm:h-48 md:w-56 md:h-56 lg:w-64 lg:h-64 border-2 border-violet-500/30 rounded-full"
            style={{
              boxShadow: '0 0 60px rgba(139, 92, 246, 0.4), inset 0 0 40px rgba(139, 92, 246, 0.2)'
            }}
          />
          <motion.div
            animate={{
              rotate: [360, 0],
              scale: [1, 1.3, 1],
            }}
            transition={{
              duration: 30,
              repeat: Infinity,
              ease: "linear"
            }}
            className="absolute top-1/2 right-1/4 w-28 h-28 sm:w-36 sm:h-36 md:w-40 md:h-40 lg:w-48 lg:h-48 border-2 border-blue-500/30 rounded-2xl"
            style={{
              boxShadow: '0 0 50px rgba(59, 130, 246, 0.4), inset 0 0 30px rgba(59, 130, 246, 0.2)'
            }}
          />
          <motion.div
            animate={{
              rotate: [0, -180, 0],
              scale: [1, 1.15, 1],
            }}
            transition={{
              duration: 28,
              repeat: Infinity,
              ease: "linear"
            }}
            className="absolute bottom-1/4 left-1/2 w-32 h-32 sm:w-40 sm:h-40 md:w-48 md:h-48 lg:w-56 lg:h-56 border-2 border-pink-500/30"
            style={{
              boxShadow: '0 0 55px rgba(236, 72, 153, 0.4), inset 0 0 35px rgba(236, 72, 153, 0.2)'
            }}
          />
        </div>

        {/* Content */}
        <motion.div
          style={{ opacity, scale, y }}
          className="relative z-10 text-center px-6 max-w-6xl mx-auto"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, delay: 0.2, ease: 'easeOut' }}
            className="inline-flex items-center gap-2 px-4 py-2 border border-violet-500/30 rounded-full mb-8 backdrop-blur-sm neon-border"
            style={{
              boxShadow: '0 0 20px rgba(139, 92, 246, 0.2), inset 0 0 20px rgba(139, 92, 246, 0.1)'
            }}
          >
            <Sparkles size={16} className="text-violet-400" />
            <span className="text-sm text-white/70 tracking-wide">AI-Powered Creative Studio</span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.4, ease: 'easeOut' }}
            className="text-5xl md:text-7xl lg:text-8xl font-light tracking-tight leading-tight mb-8"
            style={{ letterSpacing: '-0.02em' }}
          >
            {studioInfo.hero.title}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.6, ease: 'easeOut' }}
            className="text-lg md:text-xl text-white/70 mb-12 max-w-2xl mx-auto leading-relaxed"
          >
            {studioInfo.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 0.8, ease: 'easeOut' }}
            className="flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Link to="/portfolio">
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.3 }}
                className="group inline-flex items-center gap-3 bg-white text-black px-10 py-5 rounded-xl text-sm tracking-wide hover:bg-white/90 transition-all duration-300"
                style={{
                  boxShadow: '0 0 30px rgba(255, 255, 255, 0.3)'
                }}
              >
                Explore Our Expertise
                <ArrowRight
                  size={18}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </motion.button>
            </Link>
            <Link to="/contact">
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.3 }}
                className="inline-flex items-center gap-3 border border-violet-500/40 text-white px-10 py-5 rounded-xl text-sm tracking-wide hover:bg-white/5 transition-all duration-300 backdrop-blur-sm neon-border"
                style={{
                  boxShadow: '0 0 20px rgba(139, 92, 246, 0.2)'
                }}
              >
                Get Started
              </motion.button>
            </Link>
          </motion.div>
        </motion.div>

        {/* Scroll Indicator */}
        {/*
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.5, duration: 1 }}
          className="absolute bottom-12 left-1/2 -translate-x-1/2 z-10"
        >
          <motion.div
            animate={{ y: [0, 12, 0] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
            className="w-6 h-10 border border-violet-500/40 rounded-full flex items-start justify-center p-2"
            style={{
              boxShadow: '0 0 15px rgba(139, 92, 246, 0.3)'
            }}
          >
            <motion.div
              className="w-1.5 h-1.5 bg-violet-400 rounded-full"
              style={{
                boxShadow: '0 0 8px rgba(139, 92, 246, 0.6)'
              }}
            />
          </motion.div>
        </motion.div>
        */}
      </section>

      {/* What Cultiv AI Studio is — GEO entity definition */}
      <section id="about" className="relative py-32 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-violet-950/10 to-black pointer-events-none" />
        <motion.div
          aria-hidden="true"
          animate={{ opacity: [0.15, 0.3, 0.15] }}
          transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full border border-violet-500/20"
          style={{ boxShadow: '0 0 80px rgba(139, 92, 246, 0.2)' }}
        />
        <div className="relative max-w-[1100px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: 'easeOut' }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <span
              className="inline-block text-sm text-violet-400 tracking-widest uppercase mb-6 border border-violet-500/30 px-6 py-2 rounded-full backdrop-blur-sm neon-border"
              style={{
                boxShadow: '0 0 20px rgba(139, 92, 246, 0.2), inset 0 0 20px rgba(139, 92, 246, 0.1)',
              }}
            >
              About Cultiv AI Studio
            </span>
            <h2 className="text-4xl md:text-6xl font-light tracking-tight leading-tight bg-clip-text text-transparent bg-gradient-to-r from-white via-violet-200 to-blue-200">
              What Cultiv AI Studio is
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2, ease: 'easeOut' }}
            viewport={{ once: true }}
            className="relative p-10 md:p-14 rounded-3xl border border-violet-500/20 bg-gradient-to-br from-white/[0.03] via-transparent to-blue-500/[0.04] neon-border"
            style={{
              boxShadow:
                '0 0 40px rgba(139, 92, 246, 0.15), inset 0 0 40px rgba(59, 130, 246, 0.05)',
            }}
          >
            <p className="text-lg md:text-xl text-white/80 leading-relaxed mb-6">
              Cultiv AI Studio is an AI-first creative agency that produces viral video ads, brand
              systems, product visuals, and UGC-style content for B2B SaaS, DTC, and early-stage
              teams worldwide. We combine generative AI pipelines — text-to-video, image
              generation, 3D product rendering, and voice synthesis — with senior creative
              direction so founders, marketers, and product teams can ship a full creative
              campaign in days instead of the months a traditional agency requires.
            </p>
            <p className="text-lg md:text-xl text-white/70 leading-relaxed">
              Unlike template-driven AI tools or freelance networks, Cultiv AI Studio operates as
              a single accountable studio: strategy, scripting, generation, editing, and brand
              review all run through one team, which is how we cut production cost by roughly 90%
              without losing the craft, consistency, and on-brand voice that make creative work
              actually convert.
            </p>
          </motion.div>

          {/* Trust signals strip */}
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            {[
              { stat: '6+', label: 'Flagship case studies', rgb: '139, 92, 246' },
              { stat: '90%', label: 'Production cost reduction', rgb: '59, 130, 246' },
              { stat: '48h', label: 'First-draft delivery', rgb: '236, 72, 153' },
              { stat: 'AI-native', label: 'Creative pipeline', rgb: '34, 211, 238' },
            ].map((item, index) => (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * index, ease: 'easeOut' }}
                viewport={{ once: true }}
                className="relative p-6 rounded-2xl border bg-gradient-to-br from-white/[0.03] to-transparent neon-border neon-border-hover text-center"
                style={{
                  borderColor: `rgba(${item.rgb}, 0.25)`,
                  boxShadow: `0 0 20px rgba(${item.rgb}, 0.12), inset 0 0 20px rgba(${item.rgb}, 0.05)`,
                }}
              >
                <div
                  className="text-3xl md:text-4xl font-light tracking-tight mb-2"
                  style={{ color: `rgba(${item.rgb}, 1)` }}
                >
                  {item.stat}
                </div>
                <div className="text-xs md:text-sm text-white/60 tracking-wide">
                  {item.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="relative py-32 px-6 overflow-hidden">
        <motion.div
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.05, 0.1, 0.05],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut"
          }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-violet-500/20"
          style={{
            boxShadow: '0 0 100px rgba(139, 92, 246, 0.2)'
          }}
        />

        <div className="relative max-w-[1200px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: 'easeOut' }}
            viewport={{ once: true }}
            className="text-center"
          >
            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: "120px" }}
              transition={{ duration: 1.2, delay: 0.2 }}
              viewport={{ once: true }}
              className="h-px mx-auto mb-12"
              style={{
                background: 'linear-gradient(to right, transparent, rgba(139, 92, 246, 0.6), transparent)',
                boxShadow: '0 0 20px rgba(139, 92, 246, 0.5)'
              }}
            />

            <h2 className="text-5xl md:text-7xl font-light tracking-tight mb-8 leading-tight">
              Our Mission
            </h2>

            <motion.p
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.3 }}
              viewport={{ once: true }}
              className="text-2xl md:text-3xl text-white/80 max-w-4xl mx-auto leading-relaxed font-light"
            >
              {studioInfo.mission}
            </motion.p>

            <motion.div
              initial={{ width: 0 }}
              whileInView={{ width: "120px" }}
              transition={{ duration: 1.2, delay: 0.5 }}
              viewport={{ once: true }}
              className="h-px mx-auto mt-12"
              style={{
                background: 'linear-gradient(to right, transparent, rgba(139, 92, 246, 0.6), transparent)',
                boxShadow: '0 0 20px rgba(139, 92, 246, 0.5)'
              }}
            />
          </motion.div>
        </div>
      </section>

      {/* Services Section */}
      <section className="relative py-32 px-6 overflow-hidden services-section">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-violet-950/5 to-black pointer-events-none" />

        <div className="relative max-w-[1400px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: 'easeOut' }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <motion.span
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="inline-block text-sm text-violet-400 tracking-widest uppercase mb-6 border border-violet-500/30 px-6 py-2 rounded-full backdrop-blur-sm"
              style={{
                boxShadow: '0 0 20px rgba(139, 92, 246, 0.2), inset 0 0 20px rgba(139, 92, 246, 0.1)'
              }}
            >
              Our Expertise
            </motion.span>
            <h2 className="text-5xl md:text-7xl font-light tracking-tight mb-6">
              What We Do
            </h2>
            <p className="text-xl text-white/60 max-w-2xl mx-auto">
              Cutting-edge solutions powered by artificial intelligence
            </p>
          </motion.div>

          {/* Services Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const iconColors = [
                { Icon: Palette, gradient: 'from-pink-500 to-rose-500', color: 'pink', rgb: '236, 72, 153' },
                { Icon: Zap, gradient: 'from-yellow-500 to-orange-500', color: 'blue', rgb: '59, 130, 246' },
                { Icon: Sparkles, gradient: 'from-violet-500 to-purple-500', color: 'violet', rgb: '139, 92, 246' },
                { Icon: Box, gradient: 'from-blue-500 to-cyan-500', color: 'cyan', rgb: '34, 211, 238' }
              ];

              const { Icon, gradient, color, rgb } = iconColors[index];

              return (
                <motion.div
                  key={service.id}
                  className="group relative service-card"
                >
                  <motion.div
                    whileHover={{ y: -10, scale: 1.02 }}
                    transition={{ duration: 0.4, ease: 'easeOut' }}
                    className="relative h-full p-10 border rounded-2xl bg-gradient-to-br from-white/[0.03] to-transparent neon-border neon-border-hover"
                    style={{
                      borderColor: `rgba(${rgb}, 0.2)`,
                      boxShadow: `0 0 20px rgba(${rgb}, 0.1), inset 0 0 20px rgba(${rgb}, 0.05)`
                    }}
                  >
                    <motion.div
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 1 }}
                      transition={{ duration: 0.4 }}
                      className="absolute inset-0 bg-gradient-to-br from-white/[0.05] via-transparent to-transparent pointer-events-none rounded-2xl"
                    />

                    <motion.div
                      whileHover={{ scale: 1.1, rotate: 5 }}
                      transition={{ duration: 0.3 }}
                      className={`inline-flex items-center justify-center w-16 h-16 mb-8 rounded-2xl bg-gradient-to-br ${gradient} text-white shadow-lg`}
                      style={{
                        boxShadow: `0 0 30px rgba(${rgb}, 0.5)`
                      }}
                    >
                      <Icon size={28} />
                    </motion.div>

                    <div className="relative z-10">
                      <h3 className="text-3xl font-light mb-4 tracking-tight group-hover:text-white/90 transition-colors duration-300">
                        {service.title}
                      </h3>
                      <p className="text-white/60 text-base mb-8 leading-relaxed">
                        {service.description}
                      </p>

                      <div className="space-y-3">
                        {service.features.map((feature, idx) => (
                          <motion.div
                            key={idx}
                            initial={{ opacity: 0, x: -20 }}
                            whileInView={{ opacity: 1, x: 0 }}
                            transition={{ delay: 0.1 * idx, duration: 0.6 }}
                            viewport={{ once: true }}
                            className="flex items-center gap-3 text-white/50 text-sm"
                          >
                            <motion.div
                              whileHover={{ scale: 1.3 }}
                              className="w-1.5 h-1.5 rounded-full"
                              style={{
                                background: `rgba(${rgb}, 0.8)`,
                                boxShadow: `0 0 8px rgba(${rgb}, 0.6)`
                              }}
                            />
                            <span className="group-hover:text-white/70 transition-colors duration-300">
                              {feature}
                            </span>
                          </motion.div>
                        ))}
                      </div>
                    </div>

                    <motion.div
                      initial={{ scale: 0, opacity: 0 }}
                      whileHover={{ scale: 1, opacity: 1 }}
                      transition={{ duration: 0.3 }}
                      className="absolute top-0 right-0 w-20 h-20 rounded-br-2xl"
                      style={{
                        borderTop: `1px solid rgba(${rgb}, 0.3)`,
                        borderRight: `1px solid rgba(${rgb}, 0.3)`,
                        boxShadow: `0 0 20px rgba(${rgb}, 0.3)`
                      }}
                    />
                  </motion.div>
                </motion.div>
              );
            })}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.6, ease: 'easeOut' }}
            viewport={{ once: true }}
            className="text-center mt-20"
          >
            <Link to="/portfolio" title="See all AI viral case studies">
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.3 }}
                className="group inline-flex items-center gap-3 border border-violet-500/40 text-white px-10 py-5 rounded-xl text-sm tracking-wide hover:bg-white/5 transition-all duration-300"
                style={{
                  boxShadow: '0 0 20px rgba(139, 92, 246, 0.2)'
                }}
              >
                See all AI viral case studies
                <ArrowRight
                  size={18}
                  className="transition-transform duration-300 group-hover:translate-x-1"
                />
              </motion.button>
            </Link>
            <div className="mt-6 text-sm text-white/60">
              Or{' '}
              <Link
                to="/ai-creatives"
                title="Browse the AI creatives gallery"
                className="text-violet-300 hover:text-white underline underline-offset-4 decoration-violet-400/40 hover:decoration-violet-300 transition-colors"
              >
                browse our AI creatives gallery
              </Link>{' '}
              for 100+ AI-generated videos and brand visuals.
            </div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="relative py-32 px-6 testimonials-section">
        <div className="max-w-[1400px] mx-auto">
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: 'easeOut' }}
            viewport={{ once: true }}
            className="text-5xl md:text-6xl font-light tracking-tight mb-20 text-center"
          >
            Client Stories
          </motion.h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => {
              const colors = ['139, 92, 246', '59, 130, 246', '236, 72, 153'];
              const rgb = colors[index % 3];

              return (
                <motion.div
                  key={testimonial.id}
                  whileHover={{ y: -10 }}
                  transition={{ duration: 0.4, ease: 'easeOut' }}
                  className="group relative testimonial-card"
                >
                  <div
                    className="relative h-full p-10 border rounded-2xl bg-gradient-to-br from-white/[0.02] to-transparent neon-border neon-border-hover"
                    style={{
                      borderColor: `rgba(${rgb}, 0.2)`,
                      boxShadow: `0 0 20px rgba(${rgb}, 0.1), inset 0 0 20px rgba(${rgb}, 0.05)`
                    }}
                  >
                    <motion.div
                      initial={{ opacity: 0, scale: 0 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 0.3 + index * 0.1, duration: 0.6 }}
                      viewport={{ once: true }}
                      className="text-6xl text-white/10 font-serif mb-4 leading-none"
                    >
                      "
                    </motion.div>

                    <p className="text-white/70 text-base leading-relaxed mb-8 relative z-10">
                      {testimonial.quote}
                    </p>

                    <div className="relative z-10">
                      <div
                        className="h-px mb-6"
                        style={{
                          background: `linear-gradient(to right, rgba(${rgb}, 0.3), transparent)`,
                          boxShadow: `0 0 10px rgba(${rgb}, 0.3)`
                        }}
                      />
                      <p className="text-white font-normal text-base mb-1">{testimonial.name}</p>
                      <p className="text-white/40 text-sm">
                        {testimonial.role}, {testimonial.company}
                      </p>
                    </div>

                    <motion.div
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 1 }}
                      transition={{ duration: 0.4 }}
                      className="absolute inset-0 bg-gradient-to-tr from-white/[0.03] via-transparent to-transparent pointer-events-none rounded-2xl"
                    />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Lead Capture + FAQ Section */}
      <section className="relative py-32 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-blue-950/10 to-black pointer-events-none" />
        <div className="relative max-w-[1200px] mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: 'easeOut' }}
            viewport={{ once: true }}
          >
            <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-6 leading-tight">
              Get a free AI ad creative audit
            </h2>
            <p className="text-lg text-white/70 leading-relaxed mb-8">
              Share your product and a couple of current ads. We&rsquo;ll send back a short,
              founder-friendly breakdown of what&rsquo;s working, what to kill, and where AI-native
              creative could cut your cost per result.
            </p>
            <LeadCaptureForm variant="card" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15, ease: 'easeOut' }}
            viewport={{ once: true }}
          >
            <FAQ
              faqs={homepageFaqs}
              title="Frequently asked questions"
              id="home-faq"
              schemaKey="home-faq"
            />
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-40 px-6 overflow-hidden">
        <motion.div
          animate={{
            rotate: [0, 360],
            scale: [1, 1.1, 1],
          }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-violet-500/20 rounded-full"
          style={{
            boxShadow: '0 0 100px rgba(139, 92, 246, 0.3)'
          }}
        />
        <motion.div
          animate={{
            rotate: [360, 0],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: 20,
            repeat: Infinity,
            ease: "linear"
          }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-blue-500/20 rounded-full"
          style={{
            boxShadow: '0 0 80px rgba(59, 130, 246, 0.3)'
          }}
        />

        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, ease: 'easeOut' }}
          viewport={{ once: true }}
          className="relative max-w-[1000px] mx-auto text-center"
        >
          <motion.div
            initial={{ scale: 0 }}
            whileInView={{ scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
            className="inline-flex items-center justify-center w-20 h-20 mb-8 border border-violet-500/30 rounded-2xl backdrop-blur-sm"
            style={{
              boxShadow: '0 0 30px rgba(139, 92, 246, 0.3), inset 0 0 30px rgba(139, 92, 246, 0.1)'
            }}
          >
            <Sparkles size={32} className="text-violet-400" />
          </motion.div>

          <h2 className="text-6xl md:text-7xl font-light tracking-tight mb-8 leading-tight">
            Let's Create Together
          </h2>
          <p className="text-xl md:text-2xl text-white/70 mb-12 max-w-2xl mx-auto leading-relaxed">
            Ready to transform your AI vision into reality?
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact" title="Book a free AI creative strategy call">
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.3 }}
                className="group inline-flex items-center gap-3 bg-white text-black px-12 py-6 rounded-xl text-lg font-medium tracking-wide hover:bg-white/90 transition-all duration-300"
                style={{
                  boxShadow: '0 0 40px rgba(255, 255, 255, 0.3)'
                }}
              >
                Book a free strategy call
                <ArrowRight
                  size={22}
                  className="transition-transform duration-300 group-hover:translate-x-2"
                />
              </motion.button>
            </Link>
            <Link to="/portfolio" title="Browse AI viral case studies">
              <motion.button
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                transition={{ duration: 0.3 }}
                className="inline-flex items-center gap-3 border border-violet-500/40 text-white px-12 py-6 rounded-xl text-lg font-medium tracking-wide hover:bg-white/5 transition-all duration-300"
                style={{
                  boxShadow: '0 0 20px rgba(139, 92, 246, 0.3)'
                }}
              >
                Browse AI viral case studies
              </motion.button>
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
    </>
  );
};

export default Home;
